import get from 'lodash/get';

import RetailManager from 'app/retail_manager/RetailManager';
import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentCreditDebitRefundViewController from 'app/src/controllers/payments/PaymentCreditDebitRefundViewController';
import PaymentCreditDebitViewController from 'app/src/controllers/payments/PaymentCreditDebitViewController';
import PaymentCreditRefundByRefViewController from 'app/src/controllers/payments/PaymentCreditRefundByRefViewController';
import PaymentDefaultViewController from 'app/src/controllers/payments/PaymentDefaultViewController';
import PaymentGiftCardViewController from 'app/src/controllers/payments/PaymentGiftCardViewController';
import PaymentManualCreditDebitViewController from 'app/src/controllers/payments/PaymentManualCreditDebitViewController';
import PaymentScanGiftCardViewController from 'app/src/controllers/payments/PaymentScanGiftCardViewController';
import PaymentSelectGiftCardViewController from 'app/src/controllers/payments/PaymentSelectGiftCardViewController';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import Format from 'app/src/data/Format';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Localize from 'app/src/localize/Localize';
import DepositPayment from 'app/src/payments/DepositPayment';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleManager from 'app/src/payments/SaleManager';
import PaymentLayout from 'app/src/views/payments/PaymentLayout';
import ManualPayment from 'payments/ManualPayment';
import Modules from 'shared_modules/Modules';

import PaymentSavedCardsViewController from './PaymentSavedCardsViewController';

export default class PaymentViewController extends BasePaymentController {
  constructor(saleManager, paymentProvider) {
    super(saleManager);

    this._paymentProvider = paymentProvider;

    // initialize the controller's managed view
    this.view = new PaymentLayout({
      el: 'div#payments_box', // attach to the existing element
      model: this._saleManager.sale,
      viewData: {
        currencySymbol: Localize.getCurrencySymbol(),
        saleData: this._saleManager.toJSON(),
        canRetrieveGateway: this._paymentProvider?.canRetrieveGateway,
      },
    });
  }

  /**
   * Initializes and displays the payment view.  Binds to sale manager events.
   */
  show() {
    super.show();

    // instantiate a child controller to manage the child view
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );

    // listen to sale manager events
    this.listenTo(this._saleManager, SaleManager.Events.PAYMENT_TOTAL_CHANGED, this.onTotalChanged);
    this.listenTo(RetailManager.events, 'all', this._onRetailManagerEvent);
  }

  /**
   * Called after the view has been rendered to set the initial state of dynamic data.
   */
  setInitialViewState() {
    this._updateViewTotal();
  }

  /**
   * Initiates a credit card deposit transaction
   *
   * @param {Decimal} amount  The deposit amount
   */
  startCreditCardDeposit(amount) {
    if (RetailManager.active && this._paymentProvider.activeGatewaySupportedTerminals) {
      RetailManager.initiateDepositTransaction(this._saleManager.sale.id, amount);
    } else {
      this.attachChildViewController(
        new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
          isDepositPayment: true,
          paymentAmount: amount,
        }),
        'content'
      );
    }
  }

  /**
   * Initiates a gift card deposit transaction
   *
   * @param {Decimal} amount The deposit amount
   */
  startGiftCardDeposit(amount) {
    this.attachChildViewController(
      new PaymentScanGiftCardViewController(this._saleManager, {
        isDepositPayment: true,
        paymentAmount: amount,
      }),
      'content'
    );
  }

  /**
   * Event handler for when the sale manager total has changed.
   */
  onTotalChanged() {
    this._updateViewTotal();
  }

  /**
   * Event handler for when the Credit/Debit Button is clicked (_Event.CREDIT_DEBIT_CLICKED),
   * and attaches the appropriate ChildViewController
   */
  onChildViewCreditDebitClicked() {
    if (
      (this._saleManager.pendingBalance.isZero() &&
        !this._paymentProvider.activeGatewayAllowsNegativePaymentRefunds) ||
      (this._paymentProvider.activeGatewayAllowsNegativePaymentRefunds &&
        ((this._saleManager.creditDebitPayments.length &&
          this._saleManager.totalCreditPayments.isZero()) ||
          !this._saleManager.creditDebitPayments.length) &&
        this._saleManager.pendingBalance.isZero())
    ) {
      PaymentServiceAlert.alert('There is no balance left to pay.');
      return;
    }

    PaymentAnalytics.trackPaymentEvent('', this._saleManager.sale.id, {
      paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
    });

    const paymentsWebFlowInManagerMode = Modules.hasModule(
      'payments.web_flow_for_ios_manager_mode'
    );

    const iOSPaymentFlowDisabled =
      paymentsWebFlowInManagerMode && this._paymentProvider.isGatewayLightspeedPayments;

    // Checks to see if All of the CardSchemeMapping require a Customer to be attached, and if so
    // Throw an error and do not let the user proceed with the sale unless a Customer is attached.
    let failsCustomerAttachedCondition = false;
    let canInitiateIOSTerminal = RetailManager.active && !RetailManager.isAndroid;
    if (!this._saleManager.options.attachedCustomer) {
      failsCustomerAttachedCondition = this._paymentProvider.activeGatewayRequiresCustomer();
    }

    if (failsCustomerAttachedCondition) {
      PaymentServiceAlert.alert(
        'You can not run a credit card until you attach a customer to the sale. Attach a customer and try again.'
      );
      this.attachChildViewController(
        new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
        'content'
      );
      return;
    }

    if (this._saleManager.isRefundSale()) {
      let creditRefundablePayments = this._saleManager.refundablePaymentList.filter((payment) => {
        let paymentType = PaymentTypeManager.getPaymentTypeByAttributes({
          paymentTypeID: payment.model.get('paymentTypeID'),
        });

        return paymentType === PaymentTypeManager.Types.TERMINAL;
      });
      if (
        this._paymentProvider.activeGatewaySupportsRefundByReference &&
        creditRefundablePayments.length
      ) {
        this.attachChildViewController(
          new PaymentCreditDebitRefundViewController(this._saleManager, this._paymentProvider),
          'content'
        );
      } else {
        // show the regular credit/debit payment view if we don't support refunds by reference
        // for the active gateway
        if (
          !iOSPaymentFlowDisabled &&
          RetailManager.active &&
          this._paymentProvider.activeGatewaySupportedTerminals
        ) {
          // Use iOS Payment Flow if feature is disabled
          RetailManager.cardReader.initiatePaymentTerminalTransaction(
            this._saleManager.sale.id.toString(),
            Number(this._saleManager.pendingBalance.toString())
          );
        } else {
          this.attachChildViewController(
            new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
              isUnreferencedRefund: true,
              iOSPaymentFlowDisabled,
              canInitiateIOSTerminal,
            }),
            'content'
          );
        }
      }
      return;
    }

    // If the app is running in Manager mode on iOS, and the payment provider supports terminals
    if (
      !iOSPaymentFlowDisabled &&
      canInitiateIOSTerminal &&
      this._paymentProvider.activeGatewaySupportedTerminals
    ) {
      RetailManager.cardReader.initiatePaymentTerminalTransaction(
        this._saleManager.sale.id.toString(),
        Number(this._saleManager.pendingBalance.toString())
      );
      return;
    }

    this.attachChildViewController(
      new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
        iOSPaymentFlowDisabled,
        canInitiateIOSTerminal,
      }),
      'content'
    );
  }

  onChildViewSavedCardsClicked(savedCards) {
    // Defensive validation: prevent saved cards payment when balance is $0 for Lightspeed Payments
    // When the deposit fix is enabled, use pendingBalance which reflects the actual
    // remaining amount to charge (including deposits). Without the flag, fall back to saleTotal.
    const useDepositFix = Modules.hasModule('customer.card_on_file_deposit_fix');
    const balanceToCheck = useDepositFix
      ? this._saleManager.pendingBalance
      : this._saleManager.saleTotal;

    if (this._paymentProvider.isGatewayLightspeedPayments && balanceToCheck.isZero()) {
      PaymentServiceAlert.alert('There is no balance left to pay.');
      return;
    }

    this.attachChildViewController(
      new PaymentSavedCardsViewController(this._saleManager, this._paymentProvider, savedCards),
      'content'
    );
  }

  /**
   * Event handler for when the Gift Card button is clicked.  Displays the gift card scan UI.
   */
  onChildViewGiftCardButtonClicked() {
    this.attachChildViewController(
      new PaymentScanGiftCardViewController(this._saleManager),
      'content'
    );
  }

  /**
   * When the PaymentCreditDebitViewController triggers the credit:debit:finished event attach a
   * PaymentDefaultViewController.
   */
  onCreditDebitFinished() {
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );
  }

  onSavedCardsFinished() {
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );
  }

  /**
   * Event handler for when a swipe payment has been taken.
   */
  onSwipePaymentReceived(payment) {
    if (
      this._saleManager.pendingBalance.isZero() &&
      !this._paymentProvider.activeGatewayHasIntegratedSwipeSupport()
    ) {
      // no more payments to take so just finish the sale
      window.merchantos.register.completeSale();
    } else {
      if (!this._paymentProvider.activeGatewayHasIntegratedSwipeSupport()) {
        flashNotifier(
          'success',
          Localize.translate(
            `Card <var>${payment.maskedDigits}</var> Payment Of <var>${Format.formatMoney(
              payment.amount
            )}</var> Added. Charge is NOT yet processed. It will be processed when you press Finish Sale.`,
            9
          )
        );
      }

      this.attachChildViewController(
        new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
        'content'
      );
    }
  }

  /**
   * Event handler for when the PaymentCreditRefundByRefViewController triggers a completion
   * event.  Attaches a PaymentDefaultController.
   */
  onRefundByRefFinished() {
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );
  }

  /**
   * Event handler for when the PaymentCreditRefundByRefViewController triggers a refund Interac
   * event. It attaches a PaymentCreditDebitViewController.
   */
  onRefundByInterac({ refundedPayment }) {
    this.attachChildViewController(
      new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
        refundedPayment,
      }),
      'content'
    );
  }

  /**
   * Event handler for when a gift card has been retrieved from the API for a payment.  Displays
   * the gift card payment view.
   *
   * @param {array} giftCards The model of the gift card to be used to make the payment
   * @param {Object} options  Options to be passed to the gift card view controller
   */
  onListGiftCard(giftCards, options) {
    this.attachChildViewController(
      new PaymentSelectGiftCardViewController(this._saleManager, giftCards, options),
      'content'
    );
  }

  /**
   * Event handler for when a gift card has been retrieved from the API for a payment.  Displays
   * the gift card payment view.
   *
   * @param {CreditAccount} giftCardModel The model of the gift card to be used to make the payment
   * @param {array} giftCardsInfo
   * @param {Object} options  Options to be passed to the gift card view controller
   */
  onGiftCardSelected(giftCardModel, giftCardsInfo, options) {
    this.attachChildViewController(
      new PaymentGiftCardViewController(this._saleManager, giftCardModel, giftCardsInfo, options),
      'content'
    );
  }

  /**
   * Event handler when the gift card payment is finished.
   */
  onGiftCardPaymentFinished() {
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );
  }

  /**
   * Event handler for when the cancel button is clicked from within the Payment Credit Debit View.
   */
  onChildViewCreditRefundCancelClicked() {
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );
  }

  /**
   * Event handler for when a Refund button is clicked from within a credit card refund view.  Displays
   * a refund by reference view for the selected payment.
   *
   * @param {Number} paymentID    The ID of the salePayment to refund
   */
  onChildViewCreditRefundRefundClicked(paymentID) {
    const payment = this._saleManager.refundablePayments.findWhere({ salePaymentID: paymentID });
    const paymentTransaction = get(payment, 'serviceModel.paymentTransaction');

    if (payment) {
      this.attachChildViewController(
        new PaymentCreditRefundByRefViewController(
          this._saleManager,
          payment,
          this._paymentProvider
        ),
        'content'
      );
    }
  }

  /**
   * Event handler from when the refund to new card button is clicked from within the Payment
   * Credit Debit View.
   */
  onChildViewCreditRefundNewCardClicked() {
    if (RetailManager.active && this._paymentProvider.activeGatewaySupportedTerminals) {
      RetailManager.cardReader.initiatePaymentTerminalTransaction(
        this._saleManager.sale.id.toString(),
        Number(this._saleManager.pendingBalance.toString())
      );
    } else {
      this.attachChildViewController(
        new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
          isUnreferencedRefund: true,
        }),
        'content'
      );
    }
  }

  /**
   * Event handler for when the cancel button is pressed from a gift card view
   */
  onChildViewCancelButtonClicked() {
    this.attachChildViewController(
      new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
      'content'
    );
  }

  /**
   * Event handler for when the Manual payment button is clicked in the PaymentCreditDebitView.
   *
   * @param {Customer} customer  The customer attached to the sale.
   * @param {boolean} isDeposit   True when the payment is for a deposit
   */
  onChildViewManualEntryButtonClicked(customer, isDeposit) {
    const amount = this._saleManager.pendingBalance;

    PaymentAnalytics.trackPaymentEvent('Manual Entry', this._saleManager.sale.id, {
      amount: amount.toString(),
      paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
    });

    /* ***************************
     * *** LEGACY CODE WARNING ***
     * ***************************
     * We check that the gateway support the Manual Entry option and it's not
     * Lightspeed Payments since we already dealing with this logic in
     * the PaymentCreditDebitViewController and the current gateway DON'T support
     * the manual entry on terminal like e.g: Cayan
     */
    if (
      this._paymentProvider.activeGateway.get('gatewayID') !==
        GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS &&
      this._paymentProvider.activeGatewaySupportsManualEntry &&
      !this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal
    ) {
      this.attachChildViewController(
        new PaymentManualCreditDebitViewController(
          this._saleManager,
          this._paymentProvider,
          customer,
          amount,
          isDeposit
        ),
        'content'
      );
    }
  }

  /**
   * Updates the view's payment total.
   *
   * @private
   */
  _updateViewTotal() {
    let paymentTotal = this._saleManager.pendingPaymentsTotal;

    if (this._saleManager.isRefundSale() && paymentTotal.isNegative()) {
      // for refunds we want the value reflected in the UI to be
      // positive
      paymentTotal = paymentTotal.negated();
    }

    this.view.updateTotal(paymentTotal);
  }

  /**
   * Event handler for Retail Manager events
   *
   * @param {string} event The triggered event
   * @param {object|string} data  The data passed along with the notification.
   * @private
   */
  _onRetailManagerEvent(event, data = null, depositAmount = null) {
    switch (event) {
      case RetailManager.Event.PAYMENT_COMPLETE:
        if (data) {
          if (data instanceof DepositPayment) {
            // We need to set the credit account ID on the payment since the Retail Manager
            // has no knowledge of the sale manager.
            data.model.set('creditAccountID', this._saleManager.creditAccountID);

            // if the payment was for a deposit then add it as such
            this._saleManager
              .addDeposit(data)
              .done(() => {
                this._saleManager.refreshSale().done(() => {
                  flashNotifier(
                    'success',
                    Localize.translate(
                      `Credit Account Deposit Of <var>${Format.formatMoney(
                        data.amount
                      )}</var> Added.`
                    ),
                    4
                  );
                });
              })
              .fail((error) => {
                PaymentServiceAlert.alert('The deposit could not be added to the sale.');
                console.error(error);
              });
          } else {
            // for this type of notification if the data is present then it should
            // be a payment.
            this._saleManager
              .addPayment(data)
              .done(() => {
                this.attachChildViewController(
                  new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
                  'content'
                );
              })
              .fail((error) => {
                PaymentServiceAlert.alert('The payment could not be added to the sale.');
                console.error(error);
              });
          }
        } else {
          // The payment has already been added so just refresh the sale data and show the
          // default payment view
          this._saleManager.refreshSale().always(() => {
            this.attachChildViewController(
              new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
              'content'
            );
          });
        }
        break;

      case RetailManager.Event.REFUND_COMPLETE:
        // The payment has already been added so just refresh the sale data and show the
        // default payment view
        this._saleManager.refreshSale().always(() => {
          this.attachChildViewController(
            new PaymentDefaultViewController(this._saleManager, this._paymentProvider),
            'content'
          );
        });
        break;

      case RetailManager.Event.NEED_MANUAL_CREDIT_CARD_UI:
        const amount = depositAmount !== null ? depositAmount : this._saleManager.pendingBalance;

        /* ***************************
         * *** LEGACY CODE WARNING ***
         * ***************************
         * We open the Manual Entry Modal if the gateway is Lightspeed Payments
         * If not, then we attach the legacy Manual Entry view
         */
        if (
          this._paymentProvider.activeGateway.get('gatewayID') ===
          GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
        ) {
          ManualPayment.doManualPayment(amount, this._saleManager, this._paymentProvider);
        } else {
          this.attachChildViewController(
            new PaymentManualCreditDebitViewController(
              this._saleManager,
              this._paymentProvider,
              this._saleManager.customer,
              amount,
              depositAmount !== null
            ),
            'content'
          );
        }

        break;

      case RetailManager.Event.NO_TERMINAL_CONFIGURED:
        if (this._saleManager.isRefundSale()) {
          if (this._paymentProvider.activeGatewaySupportsRefundByReference) {
            // if there's no terminal configured and we already have the refund
            // view displayed then we must have clicked on the refund to new card
            // button.  Make sure we show the swipe view.
            if (
              this.getAttachedChildViewController('content') instanceof
              PaymentCreditDebitRefundViewController
            ) {
              this.attachChildViewController(
                new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
                  swipeOverride: true,
                }),
                'content'
              );
            } else {
              this.attachChildViewController(
                new PaymentCreditDebitRefundViewController(
                  this._saleManager,
                  this._paymentProvider
                ),
                'content'
              );
            }
          } else {
            // show the regular credit/debit payment view if we don't support refunds by reference
            // for the active gateway
            this.attachChildViewController(
              new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
                swipeOverride: true,
                isDepositPayment: depositAmount !== null,
                paymentAmount: depositAmount,
              }),
              'content'
            );
          }
        } else {
          this.attachChildViewController(
            new PaymentCreditDebitViewController(this._saleManager, this._paymentProvider, {
              swipeOverride: true,
              isDepositPayment: depositAmount !== null,
              paymentAmount: depositAmount,
            }),
            'content'
          );
        }
        break;

      default:
        break;
    }
  }
}
